/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Oct 20 2023
 **/

import { atom } from "jotai";
import { useCallback } from "react";
import { useAtomCallback } from "jotai/utils";

import { JotaiStore } from "@/frameworks/libraries/commons";
import { Configure, Rect } from "@/frameworks/libraries/utilities";
import { useFullSizeChatManager, useMiniChatPopupManager, useSidebarActions } from "@/frameworks/core/hooks";
import { SidebarID, ZIndex } from "@/frameworks/core/commons";
import { useChatDataStore } from "@/modules/chat/store";
import { usePersistActions } from "@/frameworks/core/persistence";
import { EventHandler } from "@rfice/eventhandler";

import { useChatRoomListActions } from "../../useChatRoomList";

const lastChatAtom = atom<{
	type?: "Space" | "DM";
	channelID: string;
	spaceID?: string;
	activityMessageID?: string;
} | null>(null);

const INITIAL_MINI_CHAT_POPUP_SIZE = {
	height: 520,
	width: 320
};

type SizeMiniChatPopup = {
	width?: number;
	height?: number;
};

type OpenParams =
	| {
			channelID: string;
			spaceID: string;
			type: "Space";
			size?: SizeMiniChatPopup;
			ignoreUpdate?: boolean;
			threadID?: string;
			activityMessageID?: string;
			forceOpenType?: "fullsize" | "mini";
	  }
	| {
			channelID: string;
			type: "DM";
			forceOpenType?: "fullsize" | "mini";
			size?: SizeMiniChatPopup;
			ignoreUpdate?: boolean;
			threadID?: string;
			activityMessageID?: string;
	  };

function generateRandomCoordinates(): { y: number; x: number } {
	const x = Math.floor(Math.random() * (833 - 320 + 1)) + 320;
	const y = Math.floor(Math.random() * (340 - 60 + 1)) + 60;

	return { x, y };
}

export default function useChatVisibility() {
	const { fullSizeChatActions } = useFullSizeChatManager();
	const { miniChatPopupActions } = useMiniChatPopupManager();
	const chatRoomListActions = useChatRoomListActions();
	const sidebarActions = useSidebarActions();
	const { chats } = usePersistActions();

	function findKindTypeByChannelID(channelID: string): ChatServices.RoomType {
		const dm = chatRoomListActions.getDMRoom(channelID);

		const isDM = typeof dm !== "undefined";

		return isDM ? "DM" : "Space";
	}

	const openFullSizeChat = async (
		chatRoomData: { channelID: string; spaceID?: string },
		option: {
			chatType: "Space" | "DM";
			ignoreUpdate?: boolean;
			activityMessageID?: string;
		}
	) => {
		const { channelID, spaceID } = chatRoomData;
		const allSidebar = sidebarActions.getAll();
		const isOneDepthLeftSidebarOpened = Object.values(allSidebar).some((v) => {
			if (v.option?.direction === "right") return false;
			if (v.option?.startX !== "0px") return false;
			return v.isOpen;
		});

		if (!isOneDepthLeftSidebarOpened) {
			sidebarActions.show(SidebarID.Chat);
		}
		const openedChannelID = fullSizeChatActions.get()?.channelID;
		const isFullChatOpened = sidebarActions.isOpen(SidebarID.FullSizeChat);

		if (!option.activityMessageID && !isFullChatOpened && openedChannelID === channelID) return;

		fullSizeChatActions.show({
			channelID,
			chatType: option.chatType,
			isReady: true,
			spaceID,
			ignoreUpdate: option.ignoreUpdate,
			activityMessageID: option.activityMessageID
		});
	};

	const openMiniChatPopup = async (
		chatRoomData: { channelID: string; spaceID?: string; threadID?: string },
		option: {
			chatType: "Space" | "DM";
			size?: SizeMiniChatPopup;
			ignoreUpdate?: boolean;
		}
	) => {
		const { channelID, spaceID, threadID } = chatRoomData;
		const isOpen = miniChatPopupActions.checkIsOpen(channelID, threadID);
		if (isOpen) {
			miniChatPopupActions.focus(channelID);
			return;
		}
		const previousCoordinate = Configure.miniChatCoordinate[channelID];
		if (previousCoordinate) {
			const browserRect = new Rect({
				x: 0,
				y: 0,
				width: document.body.clientWidth,
				height: document.body.clientHeight
			});

			if (!browserRect.containsX(previousCoordinate.x)) {
				previousCoordinate.x = browserRect.size.width - INITIAL_MINI_CHAT_POPUP_SIZE.width;
			}
			if (!browserRect.containsY(previousCoordinate.y)) {
				previousCoordinate.y = browserRect.size.height - INITIAL_MINI_CHAT_POPUP_SIZE.height;
			}
		}

		const miniChatData = {
			id: channelID,
			type: option.chatType,
			spaceID,
			threadID: threadID,
			startCoordinate: previousCoordinate ?? generateRandomCoordinates(),
			zIndex: ZIndex.CHAT_POPUP + miniChatPopupActions.getAll().length,
			size: option.size,
			ignoreUpdate: option.ignoreUpdate
		};

		miniChatPopupActions.show(miniChatData);
	};

	const isFullChat = (data: boolean | "mini" | "fullsize") => {
		return typeof data === "boolean" ? data : data === "fullsize";
	};

	const dispatchChatOpenEvent = useCallback(
		(
			chatRoomData: { channelID: string; spaceID?: string; threadID?: string },
			option: {
				type?: "Space" | "DM";
				forceOpenType?: "mini" | "fullsize";
				size?: SizeMiniChatPopup;
				ignoreUpdate?: boolean;
				activityMessageID?: string;
			}
		) => {
			const { channelID, spaceID, threadID } = chatRoomData;
			const showFullSizeChat = isFullChat(
				option.forceOpenType ?? useChatDataStore.getState().show_fullsize_chat!
			);
			const chatType = option.type ?? findKindTypeByChannelID(channelID || "");
			if (showFullSizeChat) {
				openFullSizeChat(
					{ channelID, spaceID },
					{ chatType, ignoreUpdate: option.ignoreUpdate, activityMessageID: option.activityMessageID }
				);
			} else {
				sidebarActions.hide(SidebarID.PeopleDetail);
				sidebarActions.hide(SidebarID.PhoneDetail);
				sidebarActions.hide(SidebarID.FindGroupSpace);
				sidebarActions.hide(SidebarID.ChatSearch);
				openMiniChatPopup(
					{ channelID, spaceID, threadID },
					{ chatType, size: option.size, ignoreUpdate: option.ignoreUpdate }
				);
				sidebarActions.setFocus(undefined);
			}
			chats.check_has_draft(channelID, false);
		},
		[]
	);

	const open = useAtomCallback(
		useCallback(async (get, set, params?: OpenParams) => {
			const lastChat = get(lastChatAtom);
			if (!params) {
				if (!lastChat) throw Error("useChatVisibility => 없는 채팅방입니다.");
				const { channelID, spaceID } = lastChat;
				dispatchChatOpenEvent({ channelID, spaceID }, { type: lastChat.type });
				return;
			}

			if (lastChat) {
				EventHandler.fire("$Editor::Draft$", lastChat.channelID);
				const draft = chats.chat_draft(lastChat.channelID);
				if (draft) {
					chats.check_has_draft(lastChat.channelID, true);
				}
			}

			if (params.type === "DM") {
				dispatchChatOpenEvent(
					{ channelID: params.channelID, threadID: params.threadID },
					{
						type: params.type,
						forceOpenType: params.forceOpenType,
						size: params.size,
						ignoreUpdate: params.ignoreUpdate,
						activityMessageID: params.activityMessageID
					}
				);
				set(lastChatAtom, {
					channelID: params.channelID,
					type: params.type,
					activityMessageID: params.activityMessageID
				});
			}

			if (params.type === "Space") {
				const { channelID, spaceID, size } = params;
				dispatchChatOpenEvent(
					{ channelID, spaceID, threadID: params.threadID },
					{
						type: params.type,
						size,
						ignoreUpdate: params.ignoreUpdate,
						forceOpenType: params.forceOpenType,
						activityMessageID: params.activityMessageID
					}
				);
				set(lastChatAtom, {
					channelID,
					type: params.type,
					spaceID,
					activityMessageID: params.activityMessageID
				});
			}
		}, []),
		{ store: JotaiStore }
	);

	const close = useCallback(async (roomID: string) => {
		if (useChatDataStore.getState().show_fullsize_chat) {
			fullSizeChatActions.hide();
		} else {
			miniChatPopupActions.hide(roomID);
		}
	}, []);

	const closeAll = () => {
		if (!useChatDataStore.getState().show_fullsize_chat) {
			const openChatList = miniChatPopupActions.getAll();
			openChatList.map((val) => miniChatPopupActions.hide(val.id));
		}
	};

	const getLastChat = useAtomCallback(
		(get) => {
			return get(lastChatAtom);
		},
		{
			store: JotaiStore
		}
	);

	return {
		open,
		close,
		closeAll,
		getLastChat
	};
}
